import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  listIndividualAppointments,
  listIndividualAppointmentFilterOptions,
  listSubsAppointments,
  listSubsAppointmentFilterOptions,
} from "./appointments.api";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { ReadAllResponse } from "@/interfaces";
import { appointment_status } from "@/models/appointment.model";

interface FilterState {
  status: string;
  serviceParent: string;
  search: string;
  dateFrom: string;
  dateTo: string;
  sortKey: string;
  sortOrder: "asc" | "desc";
}

interface PaginationState {
  page: number;
  pageSize: number;
}

interface IndividualAppointmentFilterOptions {
  serviceParents: string[];
}

interface SubsAppointmentFilterOptions {
  serviceParents: string[];
  assigneeNames: string[];
}

interface IndividualAppointmentsState {
  list: AppointmentJoin[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: FilterState;
  pagination: PaginationState;
  options: IndividualAppointmentFilterOptions;
}

interface SubsAppointmentsState {
  list: AppointmentJoin[];
  loading: boolean;
  error: string | null;
  total: number;
  filters: FilterState;
  pagination: PaginationState;
  options: SubsAppointmentFilterOptions;
  latestFetch: string | null;
}

const initialState: IndividualAppointmentsState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    status: "",
    serviceParent: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortKey: "created_at",
    sortOrder: "desc",
  },
  pagination: { page: 1, pageSize: 50 },
  options: { serviceParents: [] },
};

export const fetchIndividualAppointments = createAsyncThunk(
  "individualAppointments/fetch",
  async (searchOverride?: string, { getState, signal } = {} as any) => {
    const s = (getState() as { individualAppointments: IndividualAppointmentsState }).individualAppointments;
    const { page, pageSize } = s.pagination;
    const { status, serviceParent, search, dateFrom, dateTo, sortKey, sortOrder } = s.filters;

    // Check if the request was cancelled
    if (signal.aborted) {
      throw new Error("Request cancelled");
    }

    return await listIndividualAppointments(
      page,
      pageSize,
      {
        status: status ? (status as appointment_status) : undefined,
        serviceParent: serviceParent || undefined,
        search: searchOverride !== undefined ? searchOverride : (search || undefined),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      },
      sortKey,
      sortOrder
    );
  }
);

export const fetchIndividualAppointmentFilterOptions = createAsyncThunk(
  "individualAppointments/fetchFilterOptions",
  async () => await listIndividualAppointmentFilterOptions()
);

// Subs appointments async thunks
export const fetchSubsAppointments = createAsyncThunk(
  "subsAppointments/fetch",
  async (searchOverride?: string, { getState, signal } = {} as any) => {
    const s = (getState() as { subsAppointments: SubsAppointmentsState }).subsAppointments;
    const { page, pageSize } = s.pagination;
    const { status, serviceParent, search, dateFrom, dateTo, sortKey, sortOrder } = s.filters;

    // Check if the request was cancelled
    if (signal.aborted) {
      throw new Error("Request cancelled");
    }

    return await listSubsAppointments(
      page,
      pageSize,
      {
        status: status ? (status as appointment_status) : undefined,
        serviceParent: serviceParent || undefined,
        search: searchOverride !== undefined ? searchOverride : (search || undefined),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      },
      sortKey,
      sortOrder
    );
  }
);

export const fetchSubsAppointmentFilterOptions = createAsyncThunk(
  "subsAppointments/fetchFilterOptions",
  async () => await listSubsAppointmentFilterOptions()
);

const slice = createSlice({
  name: "individualAppointments",
  initialState,
  reducers: {
    /* filter setters */
    setStatus(s, a: PayloadAction<string>) {
      s.filters.status = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setServiceParent(s, a: PayloadAction<string>) {
      s.filters.serviceParent = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setSearch(s, a: PayloadAction<string>) {
      s.filters.search = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setDateFrom(s, a: PayloadAction<string>) {
      s.filters.dateFrom = a.payload;
      s.pagination.page = 1;
    },
    setDateTo(s, a: PayloadAction<string>) {
      s.filters.dateTo = a.payload;
      s.pagination.page = 1;
    },

    /* pagination */
    setPage(s, a: PayloadAction<number>) {
      s.pagination.page = a.payload;
    },
    setPageSize(s, a: PayloadAction<number>) {
      s.pagination.pageSize = a.payload;
    },

    /* sorting */
    setSortKey(s, a: PayloadAction<string>) {
      if (s.filters.sortKey === a.payload) {
        s.filters.sortOrder = s.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        s.filters.sortKey = a.payload;
        s.filters.sortOrder = "asc";
      }
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchIndividualAppointments.pending, (s) => {
      s.loading = true;
    });
    b.addCase(fetchIndividualAppointments.fulfilled, (s, a: PayloadAction<ReadAllResponse<AppointmentJoin>>) => {
      s.loading = false;
      s.list = a.payload.data;
      s.total = a.payload.count ?? 0;
    });
    b.addCase(fetchIndividualAppointments.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message || "Failed to fetch appointments.";
    });

    b.addCase(
      fetchIndividualAppointmentFilterOptions.fulfilled,
      (s, a: PayloadAction<IndividualAppointmentFilterOptions>) => {
        s.options = a.payload;
      }
    );

  },
});

export const { setStatus, setServiceParent, setSearch, setDateFrom, setDateTo, setPage, setPageSize, setSortKey } = slice.actions;

// Subs appointments slice
const subsInitialState: SubsAppointmentsState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    status: "",
    serviceParent: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortKey: "created_at",
    sortOrder: "desc",
  },
  pagination: { page: 1, pageSize: 50 },
  options: { serviceParents: [], assigneeNames: [] },
  latestFetch: null,
};

const subsSlice = createSlice({
  name: "subsAppointments",
  initialState: subsInitialState,
  reducers: {
    /* filter setters */
    setSubsStatus(s, a: PayloadAction<string>) {
      s.filters.status = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setSubsServiceParent(s, a: PayloadAction<string>) {
      s.filters.serviceParent = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setSubsSearch(s, a: PayloadAction<string>) {
      s.filters.search = a.payload;
      s.pagination.page = 1; // Reset to first page when filter changes
    },
    setSubsDateFrom(s, a: PayloadAction<string>) {
      s.filters.dateFrom = a.payload;
      s.pagination.page = 1;
    },
    setSubsDateTo(s, a: PayloadAction<string>) {
      s.filters.dateTo = a.payload;
      s.pagination.page = 1;
    },

    /* pagination */
    setSubsPage(s, a: PayloadAction<number>) {
      s.pagination.page = a.payload;
    },
    setSubsPageSize(s, a: PayloadAction<number>) {
      s.pagination.pageSize = a.payload;
    },

    /* sorting */
    setSubsSortKey(s, a: PayloadAction<string>) {
      if (s.filters.sortKey === a.payload) {
        s.filters.sortOrder = s.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        s.filters.sortKey = a.payload;
        s.filters.sortOrder = "asc";
      }
    },
    updateAppointmentInList(s, a: PayloadAction<AppointmentJoin>) {
      const idx = s.list.findIndex((r) => r.id === a.payload.id);
      if (idx >= 0) s.list[idx] = a.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchSubsAppointments.pending, (s, a) => {
      s.loading = true;
      s.latestFetch = a.meta.requestId;
    });
    b.addCase(fetchSubsAppointments.fulfilled, (s, a) => {
      if (a.meta.requestId !== s.latestFetch) return;
      s.loading = false;
      s.list = a.payload.data;
      s.total = a.payload.count ?? 0;
    });
    b.addCase(fetchSubsAppointments.rejected, (s, a) => {
      if (a.meta.requestId !== s.latestFetch) return;
      s.loading = false;
      s.error = a.error.message || "Failed to fetch appointments.";
    });

    b.addCase(fetchSubsAppointmentFilterOptions.fulfilled, (s, a: PayloadAction<SubsAppointmentFilterOptions>) => {
      s.options = a.payload;
    });

  },
});

export const {
  setSubsStatus,
  setSubsServiceParent,
  setSubsSearch,
  setSubsDateFrom,
  setSubsDateTo,
  setSubsPage,
  setSubsPageSize,
  setSubsSortKey,
  updateAppointmentInList,
} = subsSlice.actions;

export const individualAppointmentsReducer = slice.reducer;
export const subsAppointmentsReducer = subsSlice.reducer;
